// ===========================================
// "Not billed" notices on the admin invoice page (#376 D5)
// ===========================================
// When an invoice was made, a child's class billed them nothing (NO_WEEKS), or a hybrid's band weeks
// were left off because no band was set (BAND_TBD). Nothing bills those weeks later by itself, so the
// invoice says so here, with what to do for THIS invoice's status, until an admin marks it handled
// (one-way). Admin only: the server attaches the notices to the admin detail route and never to a
// parent's response. Renders nothing when there is no open notice or the invoice is void.

import { Alert, AlertTitle, Box, Button, Link, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { canAmendInvoice, isCheckoutLive } from '../../utils/invoiceAmend';
import { usePermissions } from '../../hooks/usePermissions';
import { useMarkNotBilledHandled } from '../../hooks/useInvoices';
import type { Invoice, NotBilledNoticeView } from '../../api/invoices.api';

const WHAT: Record<NotBilledNoticeView['reason'], string> = {
  NO_WEEKS: 'nothing billed',
  BAND_TBD: 'band weeks not billed',
};

export default function InvoiceNotBilledNotices({ invoice }: { invoice: Invoice }) {
  // Guard first, hooks after: an invoice with no open notice (almost all of them) runs none of the
  // notice hooks, so the invoice page costs nothing extra for it.
  const notices = invoice.notBilledNotices ?? [];
  if (notices.length === 0 || invoice.status === 'CANCELLED') return null;
  return <NoticeList invoice={invoice} notices={notices} />;
}

function NoticeList({ invoice, notices }: { invoice: Invoice; notices: NotBilledNoticeView[] }) {
  const { pathname } = useLocation();
  const schoolSlug = extractSchoolSlugFromPath(pathname);
  const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';
  const { can } = usePermissions();
  const markHandled = useMarkNotBilledHandled();
  const canWrite = can('operations_invoices', 'write');

  /** What to do for THIS invoice: edit a draft, amend an unpaid sent one, else a new invoice for the weeks. */
  const remedy = (n: NotBilledNoticeView) => {
    // NO_WEEKS: there is no week to bill as things stand (W2). The fix, if any, is the data.
    if (n.reason === 'NO_WEEKS') {
      return (
        <>
          There is no week to bill as things stand: check the child&apos;s starting week and the class&apos;s weeks.
          If they are right, mark this as handled.
        </>
      );
    }
    if (invoice.status === 'DRAFT') return <>To bill these weeks, edit this invoice.</>;
    if (canAmendInvoice(invoice)) {
      // Amend is locked while the family is on the online payment page (it lapses within ~30 min).
      return isCheckoutLive(invoice) ? (
        <>To bill these weeks, amend this invoice once the family's online payment finishes or expires.</>
      ) : (
        <>To bill these weeks, amend this invoice.</>
      );
    }
    const params = new URLSearchParams({
      create: '1',
      familyId: invoice.familyId,
      termId: invoice.termId ?? '',
      studentId: n.studentId,
      enrollmentId: n.enrollmentId,
    });
    return (
      <>
        To bill these weeks,{' '}
        <Link component={RouterLink} to={`${slugPrefix}/admin/invoices?${params.toString()}`}>
          Create Invoice → Bill specific weeks
        </Link>
        .
      </>
    );
  };

  return (
    <Alert severity="warning" sx={{ mb: 3 }} data-testid="not-billed-notices">
      <AlertTitle>Not billed on this invoice</AlertTitle>
      {notices.map((n) => (
        <Box key={n.id} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
          <Typography variant="body2">
            <strong>
              {n.studentName} · {n.lessonName}
            </strong>
            : {WHAT[n.reason]} ({n.note}).
          </Typography>
          {n.reason === 'BAND_TBD' && (
            <Typography variant="body2" color="text.secondary">
              {n.bandNow
                ? `${n.bandNow.name} is now this class's band, so its weeks can be billed.`
                : 'The class still has no band.'}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {remedy(n)}
          </Typography>
          {canWrite && (
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
              disabled={markHandled.isPending}
              aria-label={`Mark ${n.studentName} · ${n.lessonName} as handled`}
              onClick={() => markHandled.mutate({ invoiceId: invoice.id, linkId: n.id })}
            >
              Mark as handled
            </Button>
          )}
        </Box>
      ))}
    </Alert>
  );
}
